import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import SharedFooter from "@/components/SharedFooter";
import {
  Award,
  Download,
  ExternalLink,
  Lock,
  Trophy,
  Star,
  Flame,
  Target,
  BookOpen,
  Medal,
  Crown,
  Sparkles,
  User,
  ArrowRight,
  Zap,
  TrendingUp,
  Gift,
  Clock,
} from "lucide-react";

const CERTIFICATE_ICONS = {
  first_steps: <Target className="w-8 h-8" />,
  knowledge_seeker: <BookOpen className="w-8 h-8" />,
  scholar: <Award className="w-8 h-8" />,
  accuracy_star: <Star className="w-8 h-8" />,
  perfect_score: <Trophy className="w-8 h-8" />,
  streak_champion: <Flame className="w-8 h-8" />,
  streak_master: <Crown className="w-8 h-8" />,
  subject_master: <Medal className="w-8 h-8" />,
  practice_pro: <Target className="w-8 h-8" />,
  study_planner: <BookOpen className="w-8 h-8" />,
};

const CERTIFICATE_ICONS_SM = {
  first_steps: <Target className="w-5 h-5" />,
  knowledge_seeker: <BookOpen className="w-5 h-5" />,
  scholar: <Award className="w-5 h-5" />,
  accuracy_star: <Star className="w-5 h-5" />,
  perfect_score: <Trophy className="w-5 h-5" />,
  streak_champion: <Flame className="w-5 h-5" />,
  streak_master: <Crown className="w-5 h-5" />,
  subject_master: <Medal className="w-5 h-5" />,
  practice_pro: <Target className="w-5 h-5" />,
  study_planner: <BookOpen className="w-5 h-5" />,
};

const CERTIFICATE_COLORS = {
  first_steps: "from-green-400 to-emerald-500",
  knowledge_seeker: "from-blue-400 to-cyan-500",
  scholar: "from-purple-400 to-violet-500",
  accuracy_star: "from-yellow-400 to-amber-500",
  perfect_score: "from-yellow-500 to-orange-500",
  streak_champion: "from-orange-400 to-red-500",
  streak_master: "from-pink-400 to-rose-500",
  subject_master: "from-indigo-400 to-purple-500",
  practice_pro: "from-teal-400 to-cyan-500",
  study_planner: "from-violet-400 to-purple-500",
};

const CERTIFICATE_TIPS = {
  first_steps: "Complete just 5 lessons to unlock this! You're almost there.",
  knowledge_seeker: "Keep learning! Complete 25 lessons to earn this badge.",
  scholar: "Dedicated learners who complete 50 lessons earn this prestigious certificate.",
  accuracy_star: "Score 80%+ accuracy across your lessons to shine bright!",
  perfect_score: "Achieve a perfect 100% in any test - you can do it!",
  streak_champion: "Maintain a 7-day learning streak to become a champion.",
  streak_master: "30 days of consistent learning earns you this crown!",
  subject_master: "Master any subject by completing all its lessons.",
  practice_pro: "Complete 10 practice tests to prove your skills.",
  study_planner: "Create and follow a study plan to earn this certificate.",
};

export default function Certificates() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [planType, setPlanType] = useState("");
  const [earnedCertificates, setEarnedCertificates] = useState([]);
  const [progress, setProgress] = useState([]);
  const [stats, setStats] = useState(null);
  const [newCertificates, setNewCertificates] = useState([]);
  const [view, setView] = useState("earned"); // earned, progress
  const navigate = useNavigate();

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else if (response.status === 401) {
        navigate("/login");
      }
    } catch (error) {
      toast.error("Failed to load profile");
    }
  }, [navigate]);

  const checkAccess = useCallback(async () => {
    try {
      const response = await fetch(`${API}/certificates/available`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setHasAccess(data.has_access);
        setPlanType(data.plan_type);
        setUpgradeMessage(data.upgrade_message);
      }
    } catch (error) {
      console.error("Access check failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEarnedCertificates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/certificates/earned`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEarnedCertificates(data.certificates || []);
        setNewCertificates(data.new_certificates || []);
        
        // Show toast for new certificates
        if (data.new_certificates?.length > 0) {
          data.new_certificates.forEach(cert => {
            toast.success(`New certificate earned: ${cert.certificate_type.replace(/_/g, " ")}`);
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch certificates:", error);
    }
  }, []);

  const fetchProgress = useCallback(async () => {
    try {
      const response = await fetch(`${API}/certificates/progress`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProgress(data.progress || []);
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Failed to fetch progress:", error);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    checkAccess();
  }, [fetchProfile, checkAccess]);

  useEffect(() => {
    if (hasAccess) {
      fetchEarnedCertificates();
      fetchProgress();
    }
  }, [hasAccess, fetchEarnedCertificates, fetchProgress]);

  const viewCertificate = (certificateId) => {
    window.open(`${API}/certificates/${certificateId}/view`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Feature locked view
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <UnifiedPortalHeader
          portalType="student"
          userName=""
          userSubtitle=""
          onLogout={() => navigate("/student/profile")}
          showUserInfo={false}
          showHomeButton={false}
          hideLogout={true}
        />

        <main className="max-w-4xl mx-auto px-4 py-8 flex-1">
          <Card className="border-0 shadow-lg">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Premium Feature
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {upgradeMessage || "Progress Certificates are available on Plus and Pro plans. Earn and download certificates for your achievements!"}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  className="bg-gradient-to-r from-amber-500 to-orange-500"
                  onClick={() => navigate("/parent/dashboard")}
                  data-testid="upgrade-btn"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/student/profile")}
                >
                  <User className="w-4 h-4 mr-2" />
                  Back to Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>

        <SharedFooter />
      </div>
    );
  }

  // Get closest certificates to completion for nudges
  const getClosestCertificates = () => {
    return progress
      .filter(p => !p.is_earned && p.percentage > 0)
      .sort((a, b) => b.percentage - a.percentage)
      .slice(0, 3);
  };

  const closestCerts = getClosestCertificates();

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <UnifiedPortalHeader
        portalType="student"
        userName=""
        userSubtitle=""
        onLogout={() => navigate("/student/profile")}
        showUserInfo={false}
        showHomeButton={false}
        hideLogout={true}
      />

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6 flex-1">
        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Hero Stats Card */}
          <Card className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white border-0 shadow-xl overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />
            <CardContent className="pt-6 pb-6 relative">
              <h2 className="text-center text-xl font-bold mb-6">Your Achievement Journey</h2>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{earnedCertificates.length}</p>
                  <p className="text-amber-100 text-sm">Certificates Earned</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{progress.filter(p => !p.is_earned).length}</p>
                  <p className="text-amber-100 text-sm">In Progress</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Flame className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats?.current_streak || 0}</p>
                  <p className="text-amber-100 text-sm">Day Streak</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Almost There Nudge or Quick Stats */}
          {closestCerts.length > 0 ? (
            <Card className="border-0 shadow-lg bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-l-emerald-500 h-fit">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Almost There!</h3>
                    <p className="text-sm text-gray-600">You are so close to earning these certificates. Keep going!</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {closestCerts.map((cert) => (
                    <div 
                      key={cert.type} 
                      className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setView("progress")}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${CERTIFICATE_COLORS[cert.type] || "from-gray-400 to-gray-500"} flex items-center justify-center text-white`}>
                        {CERTIFICATE_ICONS_SM[cert.type] || <Award className="w-5 h-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{cert.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={cert.percentage} className="flex-1 h-2" />
                          <span className="text-xs font-semibold text-emerald-600">{cert.percentage}%</span>
                        </div>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs whitespace-nowrap">
                        {cert.target - cert.current} more!
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-lg h-fit">
              <CardContent className="pt-6 pb-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-500" />
                  Start Earning Certificates
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {["first_steps", "accuracy_star", "streak_champion"].map(type => (
                    <div key={type} className="text-center p-3 bg-gray-50 rounded-xl">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${CERTIFICATE_COLORS[type]} flex items-center justify-center text-white mx-auto mb-2 opacity-60`}>
                        {CERTIFICATE_ICONS_SM[type]}
                      </div>
                      <p className="text-xs text-gray-600 capitalize font-medium">{type.replace(/_/g, " ")}</p>
                      <p className="text-xs text-gray-400 mt-1">{CERTIFICATE_TIPS[type]?.split('.')[0]}</p>
                    </div>
                  ))}
                </div>
                <Button
                  className="w-full mt-4 bg-gradient-to-r from-amber-500 to-orange-500"
                  onClick={() => navigate("/student/learn")}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Start Learning
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tab Toggle */}
        <div className="flex bg-white rounded-xl p-1.5 shadow-md">
          <Button
            variant={view === "earned" ? "default" : "ghost"}
            className={`flex-1 rounded-lg ${view === "earned" ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md" : ""}`}
            onClick={() => setView("earned")}
          >
            <Trophy className="w-4 h-4 mr-2" />
            Earned ({earnedCertificates.length})
          </Button>
          <Button
            variant={view === "progress" ? "default" : "ghost"}
            className={`flex-1 rounded-lg ${view === "progress" ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md" : ""}`}
            onClick={() => setView("progress")}
          >
            <Target className="w-4 h-4 mr-2" />
            Progress ({progress.length})
          </Button>
        </div>

        {/* Earned Certificates */}
        {view === "earned" && (
          <>
            {earnedCertificates.length === 0 ? (
              <Card className="border-0 shadow-lg overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-amber-400 to-orange-500" />
                <CardContent className="pt-10 pb-10 text-center">
                  <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-5 ring-4 ring-amber-100">
                    <Gift className="w-10 h-10 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Your Trophy Case Awaits!</h3>
                  <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                    Start your learning journey and collect amazing certificates along the way. Each one celebrates your hard work!
                  </p>
                  
                  {/* Suggested Certificates to Start */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6 max-w-md mx-auto">
                    <p className="text-sm font-medium text-gray-700 mb-3">Easiest to earn first:</p>
                    <div className="flex justify-center gap-3">
                      {["first_steps", "accuracy_star", "streak_champion"].map(type => (
                        <div key={type} className="text-center">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${CERTIFICATE_COLORS[type]} flex items-center justify-center text-white mx-auto mb-1 opacity-50`}>
                            {CERTIFICATE_ICONS_SM[type]}
                          </div>
                          <p className="text-xs text-gray-500 capitalize">{type.replace(/_/g, " ")}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    className="bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg"
                    onClick={() => navigate("/student/learn")}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Start Learning Now
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Earned Certificates */}
                <div className="space-y-4">
                  {earnedCertificates.map((cert) => (
                    <Card 
                      key={cert.id} 
                      className={`border-0 shadow-lg overflow-hidden hover:shadow-xl transition-all ${newCertificates.some(nc => nc.id === cert.id) ? "ring-2 ring-amber-400 ring-offset-2 animate-pulse" : ""}`}
                    >
                      <div className={`h-2 bg-gradient-to-r ${CERTIFICATE_COLORS[cert.certificate_type] || "from-gray-400 to-gray-500"}`} />
                      <CardContent className="pt-6 pb-6">
                        <div className="flex items-start gap-4">
                          <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${CERTIFICATE_COLORS[cert.certificate_type] || "from-gray-400 to-gray-500"} flex items-center justify-center text-white shadow-lg`}>
                            {CERTIFICATE_ICONS[cert.certificate_type] || <Award className="w-8 h-8" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-bold text-gray-900">
                                  {cert.certificate_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                </h3>
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(cert.earned_at).toLocaleDateString()}
                                </p>
                              </div>
                              {newCertificates.some(nc => nc.id === cert.id) && (
                                <Badge className="bg-amber-100 text-amber-700">
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  New!
                                </Badge>
                              )}
                            </div>
                            <Button
                              size="sm"
                              className="mt-3 bg-gradient-to-r from-gray-700 to-gray-900"
                              onClick={() => viewCertificate(cert.id)}
                              data-testid={`view-cert-${cert.id}`}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              View Certificate
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Right Column - In Progress Certificates */}
                <div>
                  <Card className="border-0 shadow-md h-fit">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-amber-500" />
                        Up Next
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        {progress
                          .filter(p => !p.is_earned)
                          .sort((a, b) => b.percentage - a.percentage)
                          .slice(0, 3)
                          .map((cert) => (
                            <div 
                              key={cert.type} 
                              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${CERTIFICATE_COLORS[cert.type] || "from-gray-400 to-gray-500"} flex items-center justify-center text-white opacity-70`}>
                                {CERTIFICATE_ICONS_SM[cert.type] || <Award className="w-5 h-5" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm">{cert.name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Progress value={cert.percentage} className="flex-1 h-2" />
                                  <span className="text-xs font-bold text-amber-600 min-w-[35px]">{cert.percentage}%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-3 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        onClick={() => setView("progress")}
                      >
                        View All Progress
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </>
        )}

        {/* Progress View */}
        {view === "progress" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {progress.length === 0 ? (
              <Card className="border-0 shadow-md col-span-2">
                <CardContent className="pt-10 pb-10 text-center">
                  <p className="text-gray-500">Loading certificate progress...</p>
                </CardContent>
              </Card>
            ) : (
              progress.map((cert) => {
                const isClose = cert.percentage >= 70 && !cert.is_earned;
                
                return (
                  <Card 
                    key={cert.type} 
                    className={`border-0 shadow-md hover:shadow-lg transition-all ${cert.is_earned ? "bg-gradient-to-r from-green-50 to-emerald-50" : isClose ? "bg-gradient-to-r from-amber-50 to-orange-50 ring-1 ring-amber-200" : ""}`}
                  >
                    {isClose && (
                      <div className="bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-semibold text-center py-1">
                        Almost there! Just {cert.target - cert.current} more to go!
                      </div>
                    )}
                    <CardContent className="pt-5 pb-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${cert.is_earned ? `bg-gradient-to-br ${CERTIFICATE_COLORS[cert.type] || "from-gray-400 to-gray-500"} text-white` : "bg-gray-100 text-gray-400"}`}>
                          {CERTIFICATE_ICONS[cert.type] || <Award className="w-7 h-7" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{cert.name}</h3>
                            {cert.is_earned && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <Trophy className="w-3 h-3 mr-1" />
                                Earned
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mb-2">{cert.description}</p>
                          
                          {/* Tip for unearned certificates */}
                          {!cert.is_earned && CERTIFICATE_TIPS[cert.type] && (
                            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-3 flex items-start gap-2">
                              <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              {CERTIFICATE_TIPS[cert.type]}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-3">
                            <Progress 
                              value={cert.percentage} 
                              className={`flex-1 h-3 ${cert.is_earned ? "[&>div]:bg-green-500" : isClose ? "[&>div]:bg-gradient-to-r [&>div]:from-amber-400 [&>div]:to-orange-500" : ""}`}
                            />
                            <span className={`text-sm font-bold min-w-[70px] text-right ${cert.is_earned ? "text-green-600" : isClose ? "text-amber-600" : "text-gray-600"}`}>
                              {cert.current}/{cert.target}
                            </span>
                          </div>
                          
                          {/* Action button for unearned */}
                          {!cert.is_earned && cert.percentage < 100 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-3"
                              onClick={() => navigate("/student/learn")}
                            >
                              Continue Learning
                              <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Back to Profile Button */}
        <div className="flex justify-center pt-4">
          <Button
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
            onClick={() => navigate("/student/profile")}
          >
            <User className="w-4 h-4 mr-2" />
            Back to Profile
          </Button>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}
